import React from 'react';
import { 
  AssignmentReturn, 
  Timeline, 
  Warning, 
  CheckCircle, 
  Cancel, 
  Help,
  ContactSupport,
  Description
} from '@mui/icons-material';

const ReturnPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white" dir="rtl">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
        
        {/* Header Section */}
        <div className="mb-12 text-center">
          <div className="inline-flex items-center justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl blur-lg opacity-30"></div>
              <div className="relative bg-gradient-to-r from-orange-500 to-red-500 p-5 rounded-2xl shadow-xl">
                <AssignmentReturn className="text-white text-4xl" />
              </div>
            </div>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            سياسة الاسترجاع والإرجاع
          </h1>
          
          <div className="h-1.5 w-32 bg-gradient-to-r from-orange-500 to-red-500 mx-auto rounded-full mb-4"></div>
          
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            آخر تحديث: {new Date().toLocaleDateString('ar-EG', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>

        {/* Important Notice */}
        <div className="mb-10 p-6 bg-gradient-to-r from-orange-50 to-red-50 border-r-4 border-orange-500 rounded-2xl">
          <div className="flex items-start gap-4">
            <Warning className="text-orange-600 text-2xl mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-xl text-gray-900 mb-3">ملاحظة هامة</h3>
              <p className="text-gray-700 leading-relaxed text-lg">
                تهدف سياسة الاسترجاع الخاصة بنا إلى ضمان رضاك الكامل عن مشترياتك. 
                نحن نتفهم أن المنتجات قد لا تناسب احتياجاتك دائمًا، ونسعى جاهدين لتوفير عملية إرجاع سهلة وشفافة.
              </p>
            </div>
          </div>
        </div>

        {/* Policy Sections */}
        <div className="space-y-8">
          {[
            {
              icon: <Timeline />,
              title: "١. فترة الاسترجاع",
              items: [
                "فترة الاسترجاع: ١٤ يومًا من تاريخ استلام المنتج",
                "يجب أن يكون المنتج في حالته الأصلية وغير مستخدم",
                "يجب الاحتفاظ بالعبوة الأصلية وجمملة الملحقات",
                "يجب أن تكون جميع الملصقات والعلامات التجارية سليمة"
              ],
              note: "فترة الـ ١٤ يومًا تبدأ من تاريخ تأكيد الاستلام في تطبيق التتبع."
            },
            {
              icon: <CheckCircle />,
              title: "٢. المنتجات القابلة للاسترجاع",
              items: [
                "المنتجات التالفة أثناء النقل (يجب الإبلاغ خلال ٤٨ ساعة)",
                "المنتجات غير المطابقة للوصف أو الصورة",
                "المنتجات ذات العيوب المصنعية",
                "المنتجات المرسلة بالخطأ"
              ],
              color: "from-green-500/20 to-green-600/20",
              borderColor: "border-green-500"
            },
            {
              icon: <Cancel />,
              title: "٣. المنتجات غير القابلة للاسترجاع",
              items: [
                "المنتجات المخصصة حسب الطلب",
                "المنتجات التالفة بسبب سوء الاستخدام",
                "المنتجات التي تم فتح عبواتها الصحية أو التجميلية",
                "الملابس الداخلية والمنتجات الصحية",
                "المنتجات الرقمية (البرامج، الكتب الإلكترونية)",
                "البطاقات الهدايا والقسائم"
              ],
              color: "from-red-500/20 to-red-600/20",
              borderColor: "border-red-500"
            },
            {
              icon: <Description />,
              title: "٤. خطوات عملية الاسترجاع",
              items: [
                "اتصل بدعم العملاء أو استخدم نموذج الاسترجاع في حسابك",
                "قدم صورًا واضحة للمنتج والعيب (إن وجد)",
                "انتظر تأكيد الموافقة على الاسترجاع",
                "احزم المنتج بعناية مع جميع الملحقات",
                "أرسل المنتج إلى العنوان المحدد",
                "تابع حالة الاسترجاع من خلال حسابك"
              ],
              note: "متوسط وقت معالجة طلبات الاسترجاع: ٣-٥ أيام عمل"
            },
            {
              icon: <Timeline />,
              title: "٥. عملية استرداد الأموال",
              items: [
                "يتم استرداد المبلغ خلال ٥-٧ أيام عمل من استلام المنتج",
                "يتم الاسترداد بنفس طريقة الدفع الأصلية",
                "رسوم الشحن الأصلية غير قابلة للاسترداد",
                "قد يتم خصم رسوم إعادة التغليف في بعض الحالات",
                "سيتم إخطارك برسالة تأكيد عند اكتمال الاسترداد"
              ],
              note: "قد تستغرق عملية الاسترداد في البطاقات الائتمانية ما يصل إلى ١٠ أيام عمل حسب البنك."
            },
            {
              icon: <Help />,
              title: "٦. الاستبدال والإصلاح",
              items: [
                "يمكن استبدال المنتج المعيب بمنتج مماثل",
                "إذا لم يكن المنتج البديل متوفرًا، سيتم استرداد المبلغ",
                "قد نقدم إصلاحًا للمنتج إذا كان ذلك ممكنًا",
                "وقت الاستبدال: ٧-١٠ أيام عمل",
                "يتم تحمل تكاليف الشحن للاستبدال من قبلنا"
              ]
            },
            {
              icon: <Warning />,
              title: "٧. شروط خاصة",
              items: [
                "يجب الإبلاغ عن التلف خلال ٤٨ ساعة من الاستلام",
                "نحتفظ بحق رفض الاسترجاع إذا لم تتحقق الشروط",
                "قد يتم خصم قيمة المنتج إذا كان تالفًا بسبب الإهمال",
                "المنتجات المقلدة أو المزورة غير مشمولة بالاسترجاع",
                "يجب أن تكون فاتورة الشراء متاحة"
              ]
            }
          ].map((section, index) => (
            <section 
              key={index} 
              className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden border border-gray-100"
            >
              <div className="p-6">
                <div className="flex items-start gap-4 mb-6">
                  <div className={`p-4 rounded-xl ${section.color || 'bg-gradient-to-r from-gray-100 to-gray-200'}`}>
                    <span className={`${section.borderColor ? 'text-gray-800' : 'text-orange-600'} text-2xl`}>
                      {section.icon}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                      {section.title}
                      <span className={`h-1 flex-1 bg-gradient-to-r ${
                        section.borderColor || 'from-orange-500 to-red-500'
                      } to-transparent rounded-full`}></span>
                    </h2>
                    
                    {section.items && (
                      <div className="space-y-3 mb-4">
                        {section.items.map((item, itemIndex) => (
                          <div key={itemIndex} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <CheckCircle className="text-green-500 text-sm mt-1 flex-shrink-0" />
                            <span className="text-gray-700 leading-relaxed">{item}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {section.note && (
                      <div className="mt-4 p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl border-r-4 border-orange-500">
                        <p className="text-gray-700 leading-relaxed">{section.note}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {section.borderColor && (
                <div className={`h-1 w-full bg-gradient-to-r ${section.borderColor} to-transparent`}></div>
              )}
            </section>
          ))}
        </div>

        {/* Contact Section */}
        <div className="mt-12 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <ContactSupport className="text-blue-600 text-3xl" />
            <h3 className="text-2xl font-bold text-gray-900">
              بحاجة إلى المساعدة في عملية الاسترجاع؟
            </h3>
          </div>
          
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            فريق الدعم لدينا جاهز لمساعدتك في أي استفسارات حول عمليات الاسترجاع والإرجاع
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="mailto:returns@bazaar-marketplace.com"
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:opacity-90 transition-opacity duration-300"
            >
              <span className="flex items-center gap-2">
                <span>البريد الإلكتروني للاسترجاع</span>
              </span>
            </a>
            <a
              href="https://wa.me/201503323006"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-300"
            >
              <span className="flex items-center gap-2">
                <span>واتساب الدعم</span>
              </span>
            </a>
            <a
              href="/contact"
              className="px-6 py-3 bg-white border border-blue-500 text-blue-500 rounded-lg hover:bg-blue-500 hover:text-white transition-all duration-300"
            >
              <span className="flex items-center gap-2">
                <span>نموذج الاتصال</span>
              </span>
            </a>
          </div>
          
          <div className="mt-6 text-sm text-gray-500">
            <p>ساعات عمل قسم الاسترجاع: ١٠ صباحًا - ٦ مساءً (بتوقيت مصر)</p>
            <p className="mt-1">متوسط وقت الرد: ٢٤ ساعة خلال أيام العمل</p>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-8 bg-white rounded-2xl p-6 border border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Help className="text-orange-500" />
            أسئلة شائعة حول الاسترجاع
          </h3>
          
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">ماذا أفعل إذا استلمت منتجًا تالفًا؟</h4>
              <p className="text-gray-600">
                التقط صورًا واضحة للتلف وأرسلها إلى support@bazaar-marketplace.com خلال ٤٨ ساعة من الاستلام. سيقوم فريقنا بالتواصل معك خلال ٢٤ ساعة عمل.
              </p>
            </div>
            
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">كم من الوقت يستغرق استرداد الأموال؟</h4>
              <p className="text-gray-600">
                من ٥ إلى ٧ أيام عمل من تاريخ استلام المنتج في مستودعاتنا. قد تستغرق بطاقات الائتمان ما يصل إلى ١٠ أيام عمل حسب البنك المصدر للبطاقة.
              </p>
            </div>
            
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">هل أتحمل تكاليف إعادة الشحن؟</h4>
              <p className="text-gray-600">
                في حالة المنتجات المعيبة أو غير المطابقة للوصف، نحن نتحمل تكاليف الشحن. في حالات أخرى، قد تتحمل رسوم إعادة الشحن.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200 text-center">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-orange-100 to-red-100 px-6 py-3 rounded-full">
              <AssignmentReturn className="text-orange-600" />
              <span className="text-orange-700 font-bold">سياسة استرجاع شفافة وعادلة</span>
            </div>
          </div>
          
          <div className="text-gray-600 text-sm leading-relaxed max-w-2xl mx-auto space-y-2">
            <p>
              نحن ملتزمون بتوفير أفضل تجربة تسوق وضمان رضاك الكامل عن منتجاتنا.
            </p>
            <p className="text-gray-500">
              آخر تحديث للسياسة: {new Date().toLocaleDateString('ar-EG')}
            </p>
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href="/policies"
              className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              السياسات العامة
            </a>
            <a
              href="/terms-conditions"
              className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              شروط الاستخدام
            </a>
            <a
              href="/contact"
              className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              اتصل بنا
            </a>
          </div>
          
          <div className="mt-6">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="px-6 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:opacity-90 transition-opacity duration-300"
            >
              العودة للأعلى
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnPolicyPage;